import { RotateCw } from "lucide-react";
import {
  ApiError,
  NetworkError,
  NotFoundError,
  ServerError,
} from "../../api/client";
import CircleBuddy, { type CircleBuddyMood } from "../CircleBuddy";

/**
 * What a failed read looks like on screen.
 *
 * A friendly, centred column rather than a red alarm box: most failures here are
 * not the student's fault and not a catastrophe — a dropped request, a page that
 * moved, a rule not yet met — so the tone is reassuring and the way forward
 * (retry, when it can help) is the loudest thing on it. No card frame: the buddy
 * and the message sit on the page, so it reads as Lume talking, not a dialog.
 *
 * The error is classified first, and the three things that follow from it — the
 * mark, what to call it, and whether retrying could possibly help — are decided
 * together:
 *
 *   NetworkError   the one failure the student can act on. The request just did
 *                  not leave; nothing is broken.
 *   NotFoundError  the content is gone or was never theirs. Retrying the exact
 *                  same request cannot change that, so no retry is offered.
 *   ServerError /  ours. Retryable — a 500 is often transient.
 *   anything else  a message the API wrote for the student, shown verbatim.
 *
 * It paints with the semantic tokens (ink / ink-soft), so it flips with the
 * caderno's light/dark theme wherever it lands.
 *
 * Every failure leads with the CircleBuddy — the buddy is Lume's face for good
 * and bad news alike, so an error screen reads as Lume talking rather than a
 * system alert. Only the mood shifts with the cause: sad when the setback is
 * ours to own (a dropped request, a 500), thinking when something could not be
 * found, pointing when it is a "not yet" the student can act on.
 */
interface Tone {
  mood: CircleBuddyMood;
  title: string;
  /** A warm line under the title — the reassuring bit, in Lume's voice */
  hint: string;
  /** Retrying an unchanged request has to be able to produce a different answer */
  retryable: boolean;
}

function classify(error: Error): Tone {
  if (error instanceof NetworkError) {
    return {
      mood: "sad",
      title: "Ficamos sem conexão",
      hint: "Parece que a internet oscilou. Quando voltar, é só tentar de novo.",
      retryable: true,
    };
  }

  if (error instanceof NotFoundError) {
    return {
      mood: "thinking",
      title: "Não encontramos isto",
      hint: "Isto pode ter sido movido ou já não está por aqui.",
      retryable: false,
    };
  }

  if (error instanceof ServerError || (error instanceof ApiError && error.isTransient)) {
    return {
      mood: "sad",
      title: "Foi coisa nossa, desculpe",
      hint: "Tivemos um tropeço do nosso lado. Costuma passar rápido — tente de novo.",
      retryable: true,
    };
  }

  if (error instanceof ApiError) {
    // A 4xx verdict — most often a 422 the API wrote for the student
    // ("Este tema ainda não tem exercícios."). It is an instruction, not a fault:
    // no retry button, because the same request gets the same answer until
    // something changes. The message carries the specifics.
    return {
      mood: "pointing",
      title: "Ainda não dá para continuar",
      hint: "Falta um passo antes disto — veja abaixo o que o Lume sugere.",
      retryable: false,
    };
  }

  // An Error from somewhere that is not the API layer at all
  return {
    mood: "sad",
    title: "Não foi possível carregar",
    hint: "Algo não saiu como esperávamos. Vamos tentar mais uma vez?",
    retryable: true,
  };
}

export default function ErrorState({
  error,
  onRetry,
  isRetrying = false,
}: {
  error: Error;
  /** Omitted when the caller has nothing to re-run — then no button is shown */
  onRetry?: () => void;
  isRetrying?: boolean;
}) {
  const tone = classify(error);
  const canRetry = tone.retryable && onRetry !== undefined;

  // The API's own message replaces the generic hint when it wrote one for the
  // student (a 422's "Termine os tópicos…"); otherwise the warm hint stands.
  const body =
    error instanceof ApiError && !(error instanceof ServerError) && error.message
      ? error.message
      : tone.hint;

  return (
    <div
      className="mx-auto mt-8 flex max-w-[380px] animate-rise flex-col items-center px-6 text-center"
      role="alert"
    >
      {/* The buddy carries its own ground shadow and idle motion, so it stands
          in the flow without a badge behind it */}
      <CircleBuddy aria-hidden="true" mood={tone.mood} size={132} />

      <p className="mt-4 text-[18px]/tight font-bold text-ink">{tone.title}</p>
      <p className="mt-2 text-[14px]/relaxed font-medium text-balance text-ink-soft">
        {body}
      </p>

      {canRetry && (
        <button
          className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-main-500 px-6 py-3 text-[14px]/none font-semibold text-white shadow-[0_10px_24px_-6px_rgba(62,69,235,0.5)] transition hover:bg-main-600 hover:shadow-[0_12px_28px_-6px_rgba(62,69,235,0.6)] disabled:cursor-not-allowed disabled:opacity-60"
          disabled={isRetrying}
          onClick={onRetry}
          type="button"
        >
          <RotateCw
            aria-hidden="true"
            className={`size-4 ${isRetrying ? "animate-spin" : ""}`}
          />
          {isRetrying ? "Tentando…" : "Tentar de novo"}
        </button>
      )}
    </div>
  );
}
